import { OutboundProcess } from '@features/supplyChain/types';
import dayjs from 'dayjs';
import { getDayDifference } from '@utils';
import { numberToLocale } from '@forms/client/AddProductForm/utils';
import { despatchLineCount } from '../../../utils/despatch';

interface OutboundShipmentNoteSummaryProps {
  selectedOutboundProcess: OutboundProcess;
  totalShippedItemsQuantity: number | undefined;
  isShippingDiscrepancy: boolean;
  shippingDiscrepancyAmount: number;
  isNegativeShippingDiscrepancy: boolean;
  hasProductLines?: boolean;
}

export function OutboundShipmentNoteSummary({
  selectedOutboundProcess,
  totalShippedItemsQuantity,
  isShippingDiscrepancy,
  shippingDiscrepancyAmount,
  isNegativeShippingDiscrepancy,
  hasProductLines = false,
}: OutboundShipmentNoteSummaryProps) {
  const lineCount = hasProductLines
    ? despatchLineCount(selectedOutboundProcess.despatchNote)
    : selectedOutboundProcess?.shipmentNote?.skus.length;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { requiredTimeOfArrival } = selectedOutboundProcess.despatchNote!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { estimatedTimeOfArrival } = selectedOutboundProcess.shipmentNote!;

  const deliveryDayDifference = getDayDifference(
    requiredTimeOfArrival.dateTime as string,
    estimatedTimeOfArrival.dateTime as string
  );

  const isLate = deliveryDayDifference < 0;

  return (
    <div className='grid grid-cols-2 gap-x-4 gap-y-3'>
      <div>
        <p className='text-sm font-semibold text-gray-900'>
          {selectedOutboundProcess?.shipmentNote?.supplierName ?? '-'}
        </p>
        <p className='text-xs text-gray-400'>Shipped with</p>
      </div>
      <div>
        <p className='text-sm font-semibold text-gray-900'>
          {dayjs(
            selectedOutboundProcess?.shipmentNote?.estimatedTimeOfArrival
              .dateTime
          )
            .tz(
              selectedOutboundProcess?.shipmentNote?.estimatedTimeOfArrival
                .timeZone
            )
            .format('DD/MM/YY, H:mm')}
          {isLate && (
            <span className='text-red-600 ml-1'>
              (+{deliveryDayDifference.toString().substring(1)}{' '}
              {deliveryDayDifference.toString().substring(1) === '1'
                ? 'day'
                : 'days'}
              )
            </span>
          )}
        </p>
        <p className='text-xs text-gray-400'>ETA</p>
      </div>
      <div>
        <p className='text-sm font-semibold text-gray-900'>
          {selectedOutboundProcess?.shipmentNote?.shipmentNoteNumber}
        </p>
        <p className='text-xs text-gray-400'>Shipment Note #</p>
      </div>
      <div>
        <p className='text-sm font-semibold text-gray-900'>
          {numberToLocale(lineCount ?? 0)}
        </p>
        <p className='text-xs text-gray-400'>Lines</p>
      </div>
      <div>
        <p className='text-sm font-semibold text-gray-900'>
          {numberToLocale(totalShippedItemsQuantity ?? 0)}
          {isShippingDiscrepancy && (
            <span
              className={
                isNegativeShippingDiscrepancy
                  ? 'text-red-600 ml-1'
                  : 'text-gray-900 ml-1'
              }
            >
              ({isNegativeShippingDiscrepancy ? '-' : '+'}
              {Math.abs(shippingDiscrepancyAmount)})
            </span>
          )}
        </p>
        <p className='text-xs text-gray-400'>
          {hasProductLines ? 'Total qty (Products)' : 'Total qty (Items)'}
        </p>
      </div>
      {selectedOutboundProcess.shipmentNote?.comment && (
        <div className='col-span-2 pt-2'>
          <p className='text-sm text-gray-700'>
            {selectedOutboundProcess.shipmentNote.comment}
          </p>
          <p className='text-xs text-gray-400'>Comment</p>
        </div>
      )}
    </div>
  );
}
